import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import {
  ApiAssociatedAccount,
  PostByTenantAccountUpdateSaveApiArg,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery,
  useGetByTenantAccountInprogressAndAccountIdentifierQuery,
  usePostByTenantAccountSubmitAndAccountIdentifierMutation,
  usePostByTenantAccountUpdateSaveMutation
} from '@/api/rtk-api'
import { TENANT } from '@/const'

import classes from './index.module.scss'
import { FormIds, SwitchOption, TransferSwitchForm } from './transfer-switch-form'
import { TransferSwitchModal } from './transfer-switch-modal'

interface ExtendedTrade extends ApiAssociatedAccount {
  accountSource: string
}
export interface TransferSwitchProps {
  businessUnitId: string
  trade: ExtendedTrade
  tradeIds: { label: string; value: string }[]
}

export const TransferSwitch = ({ businessUnitId, trade, tradeIds }: TransferSwitchProps) => {
  const [modalData, setModalData] = useState(null)
  const { formState, ...form } = useForm({
    mode: 'onBlur',
    defaultValues: {
      [FormIds.FullPartialId]: SwitchOption.Full,
      [FormIds.AmountId]: undefined,
      [FormIds.UserNameId]: '',
      [FormIds.TradeId]: ''
    }
  })

  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
    tenant: TENANT,
    accountIdentifier: trade.accountId,
    accountSource: trade.accountSource
  })

  const { data: tradingAccount, isLoading: isLoadingTradingAccount } =
    useGetByTenantAccountInprogressAndAccountIdentifierQuery({
      tenant: TENANT,
      accountIdentifier: trade.accountId
    })

  const [save, { data: saveData, isLoading: isSaveLoading, isSuccess: isSaveSuccess }] =
    usePostByTenantAccountUpdateSaveMutation()

  const [submit, { isLoading: isSubmitLoading }] =
    usePostByTenantAccountSubmitAndAccountIdentifierMutation()

  useEffect(() => {
    if (modalData && isSaveSuccess && saveData && saveData.success) {
      submit({
        accountIdentifier: modalData[FormIds.TradeId],
        tenant: TENANT
      })
      toast.success('Submission successful')
    }
  }, [isSaveSuccess, modalData, saveData, submit])

  if (isLoading || isLoadingTradingAccount) return null

  if (!data.noteValueSummary.numberActiveTrades || data.noteValueSummary.numberActiveTrades <= 1) {
    return (
      <div className={classes.switchText}>
        <h3>Unable to transfer this trade</h3>
        <p>Trade Switch is unavailable as this note has only 1 active trade</p>
      </div>
    )
  }

  const { currentlyApproving, currentlyClosing, currentlyUpdating, status } = data.account

  if (status === 'Closed') {
    return (
      <div className={classes.switchText}>
        <h3>Unable to transfer this trade</h3>
        <p>This trade is closed</p>
      </div>
    )
  }

  if (currentlyApproving || currentlyUpdating || currentlyClosing) {
    return (
      <div className={classes.switchText}>
        <h3>Unable to transfer this trade</h3>
        <p>This trade has draft operations</p>
        <p>Continue to edit, top up or withdraw this trade</p>
      </div>
    )
  }

  if (tradingAccount.isLockedForUpdates) {
    return (
      <>
        <h3>Unable to transfer this trade</h3>
        <p>This trade is Locked as it's the originating trade of a switch</p>
      </>
    )
  }

  const otherTradeIds = tradeIds.filter(({ value }) => value !== trade.accountId)

  if (otherTradeIds.length === 0) {
    return (
      <div className={classes.switchText}>
        <h3>Unable to transfer this trade</h3>
        <p>Trade Switch is unavailable as this note has no other valid trades</p>
      </div>
    )
  }

  const onModalSubmit = () => {
    const isFullSwitch = modalData[FormIds.FullPartialId] === SwitchOption.Full
    const arg: PostByTenantAccountUpdateSaveApiArg = {
      apiUnapprovedAccountUpdate: {
        accountId: modalData[FormIds.TradeId],
        accountName: modalData.accountName,
        switchRequest: {
          isFullSwitch,
          isProcessed: null,
          originatingAccountId: trade.accountId,
          switchAmount: isFullSwitch ? null : modalData[FormIds.AmountId]
        }
      },
      tenant: TENANT
    }
    save(arg)
  }

  return (
    <FormProvider formState={formState} {...form}>
      <TransferSwitchForm
        accountId={trade.accountId}
        accountValue={trade.accountValue}
        businessUnitId={businessUnitId}
        setModalData={setModalData}
        tradeIds={otherTradeIds}
      />
      <TransferSwitchModal
        amount={modalData?.[FormIds.AmountId]}
        from={trade.accountName}
        isLoading={isSaveLoading || isSubmitLoading}
        isOpen={Boolean(modalData)}
        submit={onModalSubmit}
        to={modalData?.accountName}
        onClose={() => setModalData(null)}
      />
    </FormProvider>
  )
}
